import convertToCNPJ, { formatBRLPretty, formatDate, formatPrice } from "./utils.js";
import i18n from './i18n/i18n.js';

// Column layout mirrors the broker breakdown table, prefixed with the month and
// the partner that owns each operation so a month-wide export stays readable.
function headerRow() {
  return [
    i18n.t('billing.table.colMonth'),
    i18n.t('billing.table.colPartner'),
    i18n.t('billing.breakdown.colCnpj'),
    i18n.t('billing.breakdown.colDate'),
    i18n.t('billing.breakdown.colLabel'),
    i18n.t('billing.breakdown.colClient'),
    i18n.t('billing.breakdown.colAmount'),
    i18n.t('billing.breakdown.colCcy'),
    i18n.t('billing.breakdown.colVolumeBrl'),
    i18n.t('billing.breakdown.colRevenuePartner'),
    i18n.t('billing.breakdown.colRevenueBroker'),
    i18n.t('billing.breakdown.colRevenueTotal'),
  ];
}

// The partner-facing payload only carries `revenue`; the broker one splits it
// into partner/broker/total. Same fallbacks the breakdown table applies.
function partnerRevenue(row) {
  return row.revenue_partner;
}

function brokerRevenue(row) {
  return row.revenue_broker != null ? row.revenue_broker : row.revenue;
}

function totalRevenue(row) {
  return row.revenue_total != null ? row.revenue_total : row.revenue;
}

function formatRevenue(revenue) {
  const n = Number(revenue);
  if (revenue === null || revenue === undefined || !Number.isFinite(n)) return '-';
  // need the negative sign before R$
  return n >= 0 ? `R$ ${formatBRLPretty(n)}` : `-R$ ${formatBRLPretty(-n)}`;
}

function formatVolume(volume) {
  const n = Number(volume);
  if (!Number.isFinite(n)) return '-';
  return formatPrice(n);
}

function entityRows(month, entity, brokerCnpj) {
  const isDirect = entity.cnpj === brokerCnpj;
  return (entity.breakdown || []).map(row => ([
    month,
    entity.name || '-',
    isDirect || !entity.cnpj ? '-' : convertToCNPJ(entity.cnpj),
    formatDate(row.timestamp),
    row.label || '-',
    row.client_name || '-',
    formatVolume(row.volume),
    row.ccy || '-',
    formatRevenue(row.volume_brl),
    formatRevenue(partnerRevenue(row)),
    formatRevenue(brokerRevenue(row)),
    formatRevenue(totalRevenue(row)),
  ]));
}

function slugify(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'partner';
}

function download(rows, filename) {
  const csvContent = rows
    .map(row => row.map(val => `"${String(val ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Every operation of a single partner within one month.
export function generatePartnerRevenueCSV(month, entity, brokerCnpj) {
  const rows = [headerRow(), ...entityRows(month, entity, brokerCnpj)];
  download(rows, `revenue_${month}_${slugify(entity.name)}.csv`);
}

// Every operation of every partner within one month.
export function generateMonthRevenueCSV(month, entities, brokerCnpj) {
  const rows = [headerRow()];
  (entities || []).forEach(entity => {
    rows.push(...entityRows(month, entity, brokerCnpj));
  });
  download(rows, `revenue_${month}_all_partners.csv`);
}
